import { courseForloebProgress } from '@/lib/courseAppointmentBadge';
import {
  addCivilDays,
  civilDateFromInstant,
  civilDateFromIso,
  defaultSlotSearchCivilRange,
  SLOT_SEARCH_FALLBACK_DAYS,
} from '@/lib/treatmentCourseWizard';
import type { IntervalUnit } from '@/types/treatmentCourses';

export type SlotPickerDay = {
  civil: string;
  label: string;
  weekday: string;
  hasSlots: boolean;
};

export function previousBookedStartsAtForStep(
  steps: readonly { sort_order: number; starts_at?: string | null; status?: string }[],
  sortOrder: number,
): string | null {
  const previous = [...steps]
    .filter(
      (step) =>
        step.sort_order < sortOrder &&
        Boolean(step.starts_at) &&
        step.status !== 'cancelled',
    )
    .sort((a, b) => b.sort_order - a.sort_order);
  return previous[0]?.starts_at ?? null;
}

/**
 * Default picker range for a workspace step. Unbooked steps always use
 * previous + interval ± tolerance (never a leftover booked start from
 * another step). Move mode may start from the current booking day.
 */
export function workspaceSlotSearchRange(input: {
  unbooked: boolean;
  bookedStartsAt?: string | null;
  previousStartsAt?: string | null;
  intervalValue?: number | null;
  intervalUnit?: IntervalUnit;
  toleranceValue?: number | null;
  toleranceUnit?: IntervalUnit;
  warningWindowStart?: string | null;
  warningWindowEnd?: string | null;
  nowMs?: number;
  fallbackDays?: number;
}): { rangeStart: string; rangeEnd: string } {
  const nowMs = input.nowMs ?? Date.now();
  const fallbackDays = input.fallbackDays ?? SLOT_SEARCH_FALLBACK_DAYS;
  const bookedCivil = input.unbooked ? null : civilDateFromIso(input.bookedStartsAt ?? null);
  if (bookedCivil) {
    return { rangeStart: bookedCivil, rangeEnd: addCivilDays(bookedCivil, fallbackDays) };
  }

  const intervalRange = defaultSlotSearchCivilRange({
    previousStartsAt: input.previousStartsAt,
    intervalValue: input.intervalValue,
    intervalUnit: input.intervalUnit,
    toleranceValue: input.toleranceValue,
    toleranceUnit: input.toleranceUnit,
    nowMs,
    fallbackDays,
  });
  const previousCivil = civilDateFromIso(input.previousStartsAt ?? null);
  const hasInterval = Boolean(previousCivil && (input.intervalValue ?? 0) > 0);
  if (hasInterval) return intervalRange;

  const warningStart = civilDateFromIso(input.warningWindowStart ?? null);
  const warningEnd = civilDateFromIso(input.warningWindowEnd ?? null);
  if (warningStart && warningEnd) {
    const today = civilDateFromInstant(nowMs);
    const rangeStart = warningStart < today ? today : warningStart;
    const rangeEnd = warningEnd < rangeStart ? addCivilDays(rangeStart, fallbackDays) : warningEnd;
    return { rangeStart, rangeEnd };
  }
  return intervalRange;
}

export function civilDaysInRange(rangeStart: string, rangeEnd: string): string[] {
  if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) return rangeStart ? [rangeStart] : [];
  const days: string[] = [];
  let cursor = rangeStart;
  for (let i = 0; i < 62; i += 1) {
    days.push(cursor);
    if (cursor === rangeEnd) break;
    cursor = addCivilDays(cursor, 1);
  }
  return days;
}

export function groupSlotsByCivilDay<T extends { starts_at: string }>(
  slots: readonly T[],
): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const slot of slots) {
    const civil = civilDateFromIso(slot.starts_at);
    if (!civil) continue;
    const rows = grouped.get(civil) ?? [];
    rows.push(slot);
    grouped.set(civil, rows);
  }
  for (const rows of grouped.values()) {
    rows.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  }
  return grouped;
}

export function firstSlotPickerDay(
  days: readonly string[],
  grouped: ReadonlyMap<string, unknown[]>,
): string {
  return days.find((day) => (grouped.get(day)?.length ?? 0) > 0) ?? days[0] ?? '';
}

/** Keep a user-picked day; otherwise jump to the first day that actually has slots. */
export function nextSlotPickerDay(input: {
  days: readonly string[];
  grouped: ReadonlyMap<string, unknown[]>;
  current: string;
  userPicked: boolean;
}): string {
  if (input.userPicked && input.current && input.days.includes(input.current)) {
    return input.current;
  }
  return firstSlotPickerDay(input.days, input.grouped);
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function practitionerDisplayName(
  name: string | null | undefined,
  fallback: string,
): string {
  const trimmed = name?.trim() ?? '';
  if (!trimmed || UUID_RE.test(trimmed)) return fallback;
  return trimmed;
}

export function resolvePractitionerLabel(
  id: string,
  names: ReadonlyMap<string, string>,
  fallback: string,
): string {
  return practitionerDisplayName(names.get(id) ?? id, fallback);
}

/** Empty filter value = Alle behandlere. Must be a real <option>, never missing. */
export const COURSE_SLOT_FILTER_ALL = '';

export function defaultCourseSlotPractitionerFilter(
  assignedIds: readonly string[] | null | undefined,
): string {
  const assigned = [...new Set((assignedIds ?? []).filter(Boolean))];
  if (assigned.length === 1) return assigned[0];
  return COURSE_SLOT_FILTER_ALL;
}

export function courseSlotSearchPractitionerIds(filterId: string): string[] {
  return filterId ? [filterId] : [];
}

export function restrictCourseSlotPractitionerOptions<T extends { id: string }>(
  members: readonly T[],
  eligibleIds: readonly string[] | null | undefined,
): T[] {
  if (eligibleIds == null) return [...members];
  const allowed = new Set(eligibleIds);
  return members.filter((member) => allowed.has(member.id));
}

export function courseSlotChipShowsPractitioner(filterId: string): boolean {
  return !filterId;
}

export function slotPrimaryPractitionerId(
  slot: { practitioner_ids?: readonly string[] | null } | null | undefined,
): string | null {
  const id = slot?.practitioner_ids?.[0];
  return id || null;
}

export function courseSlotChipAriaLabel(time: string, practitionerName: string, showPractitioner: boolean): string {
  if (!showPractitioner || !practitionerName) return time;
  return `${time} · ${practitionerName}`;
}

export function draftPractitionersMatchSlot(
  draftIds: readonly string[],
  slot: { practitioner_ids: readonly string[] },
): boolean {
  if (slot.practitioner_ids.length === 0) return draftIds.length === 0;
  if (draftIds.length !== slot.practitioner_ids.length) return false;
  const draft = new Set(draftIds);
  return slot.practitioner_ids.every((id) => draft.has(id));
}

export function courseSlotKey(slot: {
  starts_at: string;
  practitioner_ids?: readonly string[] | null;
}): string {
  const ids = [...(slot.practitioner_ids ?? [])].filter(Boolean).sort();
  return `${slot.starts_at}|${ids.join(',')}`;
}

export function isCourseSlotChipSelected(
  slot: { starts_at: string; practitioner_ids: readonly string[] },
  selected: { startsAt?: string | null; practitionerIds?: readonly string[] | null } | null | undefined,
): boolean {
  if (!selected?.startsAt || selected.startsAt !== slot.starts_at) return false;
  return draftPractitionersMatchSlot(selected.practitionerIds ?? [], slot);
}

export function practitionerNameMap(
  rows: readonly { id: string; name?: string | null }[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (!row.id) continue;
    const label = practitionerDisplayName(row.name, '');
    if (label) map.set(row.id, label);
  }
  return map;
}

export function portalCourseForloebProgress(
  steps: readonly { id: string; sort_order: number; status?: string }[],
): { n: number; total: number; completed: number } {
  return courseForloebProgress(steps);
}
